// Enhanced Color System for Mappin
// This file provides semantic color tokens for easy universal color management
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const darkColorBase = {
  primary: {
    50: '#050906',
    100: '#131E13',
    200: '#1E3031',
    300: '#2B494B',
    400: '#245D58',
    500: '#1D8388', // Main primary
    600: '#09B2AA',
    700: '#94E5D7',
    800: '#C1EEE2',
    900: '#EFFAF7',
  },
  neutral: {
    50: '#090909',
    100: '#161616',
    200: '#252525',
    300: '#3A3939',
    400: '#4D4B4B',
    500: '#5E5E5E',
    600: '#7A7A7A',
    700: '#AEAEAE',
    800: '#D3D3D3',
    900: '#FFFFFF',
  }
}

const lightColorBase = {
  primary: {
    900: '#161616',
    800: '#252525',
    700: '#3A3939',
    600: '#4D4B4B',
    500: '#5E5E5E',
    400: '#7A7A7A',
    300: '#AEAEAE',
    200: '#D3D3D3',
    100: '#E3E3E3',
    50: '#F8F8F8',
  },
  neutral: {
    900: '#090909',
    800: '#161616',
    700: '#252525',
    600: '#3A3939',
    500: '#4D4B4B',
    400: '#5E5E5E',
    300: '#7A7A7A',
    200: '#AEAEAE',
    100: '#D3D3D3',
    50: '#FFFFFF',
  }
}

export const lightColors = {
  border: {
    neutral: {
      lightest: lightColorBase.neutral[50],
      lighter: lightColorBase.neutral[200],
      light: lightColorBase.neutral[300],
      mid: lightColorBase.neutral[400],
      dark: lightColorBase.neutral[500],
      darker: lightColorBase.neutral[600],
      darkest: lightColorBase.neutral[700],
    },
    brand: {
      lightest: lightColorBase.primary[50],
      lighter: lightColorBase.primary[200],
      light: lightColorBase.primary[300],
      mid: lightColorBase.primary[400],
      dark: lightColorBase.primary[500],
      darker: lightColorBase.primary[700],
      darkest: lightColorBase.primary[800],
    },
  },
  
  // Text colors
  text: {
    neutral: {
      lightest: lightColorBase.neutral[50],
      lighter: lightColorBase.neutral[200],
      light: lightColorBase.neutral[300],
      mid: lightColorBase.neutral[400],
      dark: lightColorBase.neutral[500],
      darker: lightColorBase.neutral[600],
      darkest: lightColorBase.neutral[700],
    },
    brand: {
      lightest: lightColorBase.primary[50],
      lighter: lightColorBase.primary[200],
      light: lightColorBase.primary[300],
      mid: lightColorBase.primary[400],
      dark: lightColorBase.primary[600],
      darker: lightColorBase.primary[800],
      darkest: lightColorBase.primary[900],
    },
    inverse: {
      darkest: lightColorBase.primary[50],
      darker: lightColorBase.primary[200],
      dark: lightColorBase.primary[300],
      mid: lightColorBase.primary[400],
      light: lightColorBase.primary[500],
      lighter: lightColorBase.primary[700],
      lightest: lightColorBase.primary[800],
    },
    success: {
      darkest: "#A1E4A0",
      dark: "#68BC67",
      mid: "#447843",
      light: "#2E522E",
      lightest: "#1C311C",
    },
    error: {
      darkest: "#E4A0A0",
      dark: "#BC6767",
      mid: "#992C2C",
      light: "#772222",
      lightest: "#3C1111",
    },
  },
  
  // Background colors
  background: {
    neutral: {
      lightest: lightColorBase.neutral[50],
      lighter: lightColorBase.neutral[200],
      light: lightColorBase.neutral[300],
      mid: lightColorBase.neutral[400],
      dark: lightColorBase.neutral[500],
      darker: lightColorBase.neutral[600],
      darkest: lightColorBase.neutral[700],
    },
    brand: {
      lightest: lightColorBase.primary[50],
      lighter: lightColorBase.primary[200],
      light: lightColorBase.primary[300],
      mid: lightColorBase.primary[400],
      dark: lightColorBase.primary[500],
      darker: lightColorBase.primary[700],
      darkest: lightColorBase.primary[800],
    },
  },
  gradient: {
    cyan: '#5CE1E6',
    green: '#7ED957'
  }
} as const;


export const darkColors = {
  border: {
    neutral: {
      lightest: lightColorBase.neutral[50],
      lighter: darkColorBase.neutral[200],
      light: darkColorBase.neutral[300],
      mid: darkColorBase.neutral[400],
      dark: darkColorBase.neutral[500],
      darker: darkColorBase.neutral[600],
      darkest: darkColorBase.neutral[700],
    },
    brand: {
      lightest: darkColorBase.primary[50],
      lighter: darkColorBase.primary[200],
      light: darkColorBase.primary[300],
      mid: darkColorBase.primary[400],
      dark: darkColorBase.primary[500],
      darker: darkColorBase.primary[700],
      darkest: darkColorBase.primary[800],
    },
  },
  
  // Text colors
  text: {
    neutral: {
      lightest: darkColorBase.neutral[50],
      lighter: darkColorBase.neutral[200],
      light: darkColorBase.neutral[300],
      mid: darkColorBase.neutral[400],
      dark: darkColorBase.neutral[500],
      darker: darkColorBase.neutral[600],
      darkest: darkColorBase.neutral[700],
    },
    brand: {
      lightest: darkColorBase.primary[50],
      lighter: darkColorBase.primary[200],
      light: darkColorBase.primary[300],
      mid: darkColorBase.primary[400],
      dark: darkColorBase.primary[600],
      darker: darkColorBase.primary[800],
      darkest: darkColorBase.primary[900],
    },
    inverse: {
      darkest: darkColorBase.primary[50],
      darker: darkColorBase.primary[200],
      dark: darkColorBase.primary[300],
      mid: darkColorBase.primary[400],
      light: darkColorBase.primary[500],
      lighter: darkColorBase.primary[700],
      lightest: darkColorBase.primary[800],
    },
    success: {
      darkest: "#A1E4A0",
      dark: "#68BC67",
      mid: "#447843",
      light: "#2E522E",
      lightest: "#1C311C",
    },
    error: {
      darkest: "#E4A0A0",
      dark: "#BC6767",
      mid: "#992C2C",
      light: "#772222",
      lightest: "#3C1111",
    },
  },
  
  // Background colors
  background: {
    neutral: {
      lightest: darkColorBase.neutral[50],
      lighter: darkColorBase.neutral[200],
      light: darkColorBase.neutral[300],
      mid: darkColorBase.neutral[400],
      dark: darkColorBase.neutral[500],
      darker: darkColorBase.neutral[600],
      darkest: darkColorBase.neutral[700],
    },
    brand: {
      lightest: darkColorBase.primary[50],
      lighter: darkColorBase.primary[200],
      light: darkColorBase.primary[300],
      mid: darkColorBase.primary[400],
      dark: darkColorBase.primary[500],
      darker: darkColorBase.primary[700],
      darkest: darkColorBase.primary[800],
    },
  },
  gradient: {
    cyan: '#5CE1E6',
    green: '#7ED957'
  }
} as const;

// Theme context
type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  colors: typeof darkColors;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'dark',
  toggleTheme: () => {},
  colors: darkColors,
});

// Local storage key for theme preference
const THEME_STORAGE_KEY = 'mappin-theme-mode';

// Theme provider component
interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Initialize theme from local storage or default to dark
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY);
    return (savedMode as ThemeMode) || 'dark';
  });

  // Get the appropriate colors based on the current theme mode
  const colors = mode === 'light' ? lightColors : darkColors;

  // Toggle between light and dark mode
  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem(THEME_STORAGE_KEY, newMode);
      return newMode;
    });
  };

  // Update document body class for global CSS targeting
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode');
    document.body.classList.add(`${mode}-mode`);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

// Export colors for backward compatibility
export const colors = darkColors;

